import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Star, Mail } from 'react-feather';

// Plan IDs that mean the user is already on a First Mover / discounted plan
// and should NOT see the migration offer. Anyone on these plans is already
// at the best available rate and shouldn't be re-pitched. Includes the
// André-era plan IDs (for any user who subscribed during that window) plus
// the current Chargebee plan IDs.
const FIRST_MOVER_PLAN_IDS = [
  // Current Chargebee plan IDs
  'First-Mover-Network-Monthly',
  'First-Mover-Network-Annual',
  'First-Mover-Verified-Monthly',
  'First-Mover-Verified-Annual',
  'Legacy-Subscriber---Lifetime',
  // Legacy / André-era plan IDs (kept for backward compat with any
  // existing subscriptions that still reference these names)
  'viveka-network-firstmover',
  'viveka-verified-lifetime-firstmover',
  'Legacy-Subscriber---Grandfathered',
];

// Once-per-browser-session dismissal flag. We deliberately use
// sessionStorage (not localStorage) so the popup will reappear in the
// next browser session — coaches need a few touches to convert and
// hiding the offer forever after one dismissal undermines the campaign.
// If we want a per-day cooldown later, swap to localStorage with a
// timestamp.
const SESSION_KEY = 'viveka:migrationPopupShown';

/**
 * SubscriptionMigrationBanner (modal popup variant)
 *
 * Pop-up shown once per browser session to legacy subscribers (anyone
 * with an existing subscription that is NOT a First Mover plan). It
 * surfaces the regular Viveka Coach pricing — $19/mo Network Member,
 * $49/mo Verified Coach (matches coach.viveka.world/join-network) —
 * and reminds them to use the 50% off coupon code that's delivered by
 * email to legacy members.
 *
 * Render conditions (popup eligible when ALL are true):
 *   - currentUser is loaded
 *   - currentUser is a coach (isProvider === true)
 *   - currentUser has subscriptionDetails.subscriptionName on metadata
 *     (i.e. they're a paying subscriber today, not a brand-new signup)
 *   - subscriptionName is NOT a First Mover plan
 *   - sessionStorage flag has NOT been set (haven't dismissed it yet
 *     this browser session)
 *
 * The CTA navigates to /account/payments/pricing — regular pricing,
 * NOT the First Mover funnel. The 50% legacy discount is applied as
 * a Chargebee coupon at checkout (delivered separately by email).
 *
 * Visual design:
 *   - Centered modal card on a dark backdrop with click-to-close.
 *   - Forest-green-to-deep-purple gradient (cross-brand: marketplace
 *     base + coach surface) with subtle radial highlight.
 *   - Animated SVG sparkle confetti — pure CSS keyframes, no library.
 *   - Gold "EXCLUSIVE OFFER FOR LEGACY MEMBERS" eyebrow pill.
 *   - "Welcome back, **legacy member**." headline (gold accent on the
 *     two-word emphasis, matching the coach.viveka.world/first-mover
 *     pattern).
 *   - Two side-by-side plan cards with the website's join-network
 *     prices ($19 / $49).
 *   - Yellow-amber callout: "Check your inbox for your 50% off coupon".
 *   - Pulsing gold primary CTA + secondary "Maybe later" link.
 *
 * Accessibility:
 *   - Escape key closes the modal.
 *   - Backdrop click closes the modal.
 *   - role="dialog", aria-modal="true", aria-labelledby on the heading.
 *   - Body scroll is locked while the popup is open so the page behind
 *     doesn't scroll away from the user.
 */

const SPARKLES = [
  { top: '10%', left: '6%',  size: 14, color: '#FBBF24', delay: '0s',   duration: '3s'   },
  { top: '18%', left: '20%', size: 8,  color: '#FFFFFF', delay: '0.6s', duration: '2.4s' },
  { top: '78%', left: '10%', size: 10, color: '#FCD34D', delay: '1.2s', duration: '2.8s' },
  { top: '40%', left: '28%', size: 7,  color: '#FBBF24', delay: '1.8s', duration: '2.2s' },
  { top: '88%', left: '34%', size: 12, color: '#FFFFFF', delay: '0.3s', duration: '3.2s' },
  { top: '8%',  left: '52%', size: 10, color: '#FBBF24', delay: '0.9s', duration: '2.6s' },
  { top: '62%', left: '50%', size: 8,  color: '#FCD34D', delay: '1.5s', duration: '3s'   },
  { top: '22%', left: '66%', size: 14, color: '#FFFFFF', delay: '0.4s', duration: '2.8s' },
  { top: '85%', left: '70%', size: 9,  color: '#FBBF24', delay: '1.1s', duration: '2.4s' },
  { top: '14%', left: '88%', size: 11, color: '#FCD34D', delay: '0.7s', duration: '3s'   },
  { top: '50%', left: '94%', size: 7,  color: '#FFFFFF', delay: '1.6s', duration: '2.6s' },
  { top: '90%', left: '90%', size: 12, color: '#FBBF24', delay: '0.2s', duration: '3.2s' },
];

const SparkleStar = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block' }} aria-hidden="true">
    <path d="M12 2 L13.5 9 L20 10.5 L13.5 12 L12 19 L10.5 12 L4 10.5 L10.5 9 Z" fill={color} />
  </svg>
);

const PlanCard = ({ name, price, tagline }) => (
  <div
    style={{
      flex: '1 1 0%',
      minWidth: '180px',
      padding: '18px 20px',
      borderRadius: '12px',
      background: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.14)',
      backdropFilter: 'blur(2px)',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#FBBF24',
        marginBottom: '6px',
      }}
    >
      {name}
    </div>
    <div
      style={{
        fontSize: '28px',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#FFFFFF',
        lineHeight: 1.1,
      }}
    >
      {price}
      <span style={{ fontSize: '14px', fontWeight: 500, color: '#CBD5E1', marginLeft: '4px' }}>
        /month
      </span>
    </div>
    <div style={{ fontSize: '12px', color: '#CBD5E1', marginTop: '6px', lineHeight: 1.4 }}>
      {tagline}
    </div>
  </div>
);

const SubscriptionMigrationBanner = ({ currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);

  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  const subscriptionName =
    currentUser?.attributes?.profile?.metadata?.subscriptionDetails?.subscriptionName;
  const isEligible =
    !!currentUser &&
    isProvider === true &&
    !!subscriptionName &&
    !FIRST_MOVER_PLAN_IDS.includes(subscriptionName);

  // Open the popup once per browser session for eligible users.
  // sessionStorage may throw in private-mode browsers — fall back to
  // showing the popup so we don't silently swallow the offer.
  useEffect(() => {
    if (!isEligible) return;
    try {
      const alreadyShown = window.sessionStorage.getItem(SESSION_KEY);
      if (!alreadyShown) {
        setIsOpen(true);
      }
    } catch (e) {
      setIsOpen(true);
    }
  }, [isEligible]);

  // Escape key closes the popup.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeydown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock body scroll while the popup is open. This prevents the
  // dashboard behind the modal from drifting when the user scrolls
  // inside the popup (and on iOS, the page jumping under a fixed
  // overlay is a notorious foot-gun).
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      // sessionStorage unavailable; popup will simply re-open on next
      // mount, which is acceptable.
    }
  };

  // Backdrop click closes; click inside the card does not (we stop
  // propagation on the card itself).
  const handleBackdropClick = () => handleClose();

  if (!isEligible || !isOpen) return null;

  return (
    <React.Fragment>
      <style>{`
        @keyframes migrationPopupTwinkle {
          0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); }
          50%      { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        @keyframes migrationPopupShimmer {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes migrationPopupPulseGlow {
          0%, 100% {
            box-shadow:
              0 4px 20px rgba(251, 191, 36, 0.45),
              0 0 0 0 rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow:
              0 6px 28px rgba(251, 191, 36, 0.6),
              0 0 0 8px rgba(251, 191, 36, 0);
          }
        }
        @keyframes migrationPopupFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes migrationPopupSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="migration-popup-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'migrationPopupFadeIn 0.25s ease-out',
        }}
      >
        {/* Card */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '640px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            padding: '32px',
            borderRadius: '20px',
            background:
              'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.32) 0%, rgba(139, 92, 246, 0) 55%), ' +
              'linear-gradient(135deg, #064E3B 0%, #0F3F4A 45%, #1E1040 100%)',
            backgroundSize: '200% 200%',
            animation:
              'migrationPopupShimmer 14s ease-in-out infinite, migrationPopupSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
          }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'background 0.15s ease, transform 0.15s ease',
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.transform = 'scale(1.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={18} strokeWidth={2.5} />
          </button>

          {/* Sparkle confetti layer */}
          <div
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', borderRadius: '20px' }}
            aria-hidden="true"
          >
            {SPARKLES.map((s, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: s.top,
                  left: s.left,
                  animation: `migrationPopupTwinkle ${s.duration} ease-in-out ${s.delay} infinite`,
                  opacity: 0,
                }}
              >
                <SparkleStar size={s.size} color={s.color} />
              </div>
            ))}
          </div>

          {/* Content (positioned above the sparkle layer) */}
          <div style={{ position: 'relative', textAlign: 'center' }}>
            {/* Eyebrow pill */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 14px',
                borderRadius: '999px',
                border: '1px solid rgba(251, 191, 36, 0.7)',
                color: '#FBBF24',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '16px',
                background: 'rgba(251, 191, 36, 0.08)',
              }}
            >
              <Star size={11} fill="#FBBF24" stroke="none" />
              Exclusive Offer for Legacy Members
              <Star size={11} fill="#FBBF24" stroke="none" />
            </div>

            {/* Headline */}
            <h2
              id="migration-popup-title"
              style={{
                margin: 0,
                fontSize: '30px',
                fontWeight: 800,
                lineHeight: 1.15,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
              }}
            >
              Welcome back, <span style={{ color: '#FBBF24' }}>legacy member</span>.
            </h2>

            {/* Body */}
            <p
              style={{
                margin: '12px auto 0',
                color: '#E5E7EB',
                fontSize: '15px',
                lineHeight: 1.55,
                maxWidth: '480px',
              }}
            >
              Your subscription is ending soon. Continue your journey on Viveka with full coach
              membership — and use your <strong style={{ color: '#FBBF24', fontWeight: 800 }}>exclusive 50% legacy discount</strong> at checkout.
            </p>

            {/* Plan cards */}
            <div
              style={{
                display: 'flex',
                gap: '14px',
                marginTop: '24px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <PlanCard
                name="Network Member"
                price="$19"
                tagline="Profile, marketplace visibility, and access to coaching opportunities."
              />
              <PlanCard
                name="Verified Coach"
                price="$49"
                tagline="Everything in Network plus verification, priority placement, and enterprise access."
              />
            </div>

            {/* Coupon callout */}
            <div
              style={{
                marginTop: '20px',
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(251, 191, 36, 0.12)',
                border: '1px dashed rgba(251, 191, 36, 0.55)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                color: '#FCD34D',
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'left',
                maxWidth: '480px',
              }}
            >
              <Mail size={18} strokeWidth={2.2} style={{ flexShrink: 0 }} />
              <span>
                Check your inbox — your <strong style={{ color: '#FBBF24', fontWeight: 800 }}>50% off legacy member coupon</strong> is on its way. Apply it at checkout to lock in your discount.
              </span>
            </div>

            {/* CTAs */}
            <div
              style={{
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Link
                to="/account/payments/pricing"
                onClick={handleClose}
                style={{
                  backgroundColor: '#FBBF24',
                  color: '#064E3B',
                  fontWeight: 800,
                  padding: '14px 26px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  border: 'none',
                  animation: 'migrationPopupPulseGlow 2.4s ease-in-out infinite',
                  transition: 'transform 0.18s ease, filter 0.18s ease',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                  e.currentTarget.style.filter = 'brightness(1.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                <Star size={15} fill="#064E3B" stroke="none" />
                Choose Your Plan
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>

              <button
                type="button"
                onClick={handleClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  fontSize: '13px',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(SubscriptionMigrationBanner);
